import { useQuery } from "@apollo/client";
import { ArrowSmLeftIcon } from "@heroicons/react/outline";
import { withErrorBoundary } from "@sentry/react";
import { isNumber } from "@tiptap/core";
import { useCallback, useContext } from "react";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  GetPreviousRecurringMeetingQueryQuery,
  GetPreviousRecurringMeetingQueryQueryVariables,
  MeetingViewMeetingNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import getPreviousRecurringMeetingQuery from "@apps/meeting-new/graphql/get-previous-recurring-meeting-query";
import MeetingOverview from "@apps/meetings/components/meetings-overview";
import { currentUserVar } from "@cache/cache";
import AppError from "@components/error/error";
import Loading from "@components/loading/loading";
import {
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";

import { MeetingPageContent } from "../meeting-page";
import { MeetingPageContext } from "../meeting-page-context";
import MeetingSidebarRecurringMeetings from "./recurring-meetings";

const MeetingSidebarMeetings = ({
  meeting,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const { sidebarMeetingView, setSidebarMeetingView } =
    useContext(MeetingPageContext);
  if (sidebarMeetingView === undefined || setSidebarMeetingView === undefined) {
    throw new Error(
      "sidebarMeetingView && setSidebarMeetingView needs to be defined",
    );
  }

  const currentUser = currentUserVar();
  const selectedUser: UserComboboxUserOption = {
    type: UserComboboxOptionType.USER,
    name: currentUser.name,
    id: currentUser.id,
  };

  const { loading, error, data } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    skip:
      sidebarMeetingView === "overview" ||
      sidebarMeetingView === "load-previous-meeting",
    variables: {
      meetingId: isNumber(sidebarMeetingView) ? sidebarMeetingView : -1,
    },
  });
  useQuery<
    GetPreviousRecurringMeetingQueryQuery,
    GetPreviousRecurringMeetingQueryQueryVariables
  >(getPreviousRecurringMeetingQuery, {
    skip: sidebarMeetingView !== "load-previous-meeting",
    variables: { beforeMeetingId: meeting.id },

    onCompleted: (data) => {
      setSidebarMeetingView(data.previousMeeting?.id ?? "overview");
    },
  });
  const sidebarMeeting = data?.meeting;
  const sidebarMeetingGroup = sidebarMeeting?.meetingGroup;

  const handleChangeMeetingId = useCallback((id: number) => {
    setSidebarMeetingView(id);
  }, []);

  return (
    <div className="">
      {sidebarMeetingView === "overview" ? (
        <MeetingOverview
          selectedUser={selectedUser}
          embeddedInSidebar
          filterClassName="px-6"
          meetingListClassName="px-6"
          onClickMeetingLink={handleChangeMeetingId}
        />
      ) : sidebarMeetingView === "load-previous-meeting" ? (
        <Loading size={5} className="p-6 text-sm">
          Loading meeting...
        </Loading>
      ) : (
        <>
          <button
            className="flex items-center gap-2 w-full text-left px-6 py-4 text-sm text-gray-500 hover:underline border-b font-medium"
            onClick={() => setSidebarMeetingView("overview")}
          >
            <ArrowSmLeftIcon className="w-4 h-4" />
            Find another meeting
          </button>
          {loading ? (
            <Loading size={5} className="p-6 text-sm">
              Loading meeting...
            </Loading>
          ) : error || !sidebarMeeting || !sidebarMeetingGroup ? (
            <AppError
              className="p-6"
              title="Meeting not found"
              description={error?.message}
            />
          ) : (
            <>
              {sidebarMeeting.isRecurring && (
                <MeetingSidebarRecurringMeetings
                  meeting={sidebarMeeting}
                  onChangeMeetingId={handleChangeMeetingId}
                />
              )}
              <div className="p-6">
                <MeetingPageContext.Provider
                  value={{
                    isInExtension: false,
                    isInSidebar: true,
                  }}
                >
                  <MeetingPageContent
                    meetingGroupId={sidebarMeetingGroup.id}
                    meeting={sidebarMeeting}
                  />
                </MeetingPageContext.Provider>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const MeetingSidebarMeetingsWithSentry = withErrorBoundary(
  MeetingSidebarMeetings,
  {
    fallback: (
      <AppError description={"The meetings tab could not be displayed."} />
    ),
  },
);

export default MeetingSidebarMeetingsWithSentry;
